import {
  PLAN_DISPLAY,
  type PlanDisplay,
  type PlanFeature,
  type SelfServePlanKey,
} from "@giftforward/entitlements";
import CheckIcon from "@mui/icons-material/Check";

/**
 * The Starter / Pro / Pro Plus comparison shown at signup.
 *
 * Renders from `PLAN_DISPLAY` in `@giftforward/entitlements` — a plain import, no hook, no request,
 * no loading state and nothing to fail. The copy is static marketing text known at build time, and
 * the API sends this same object to the browser anyway, so fetching it bought nothing and cost the
 * whole screen: `GET /plans` requires a local user row, and at signup there isn't one yet, so the
 * catalogue 401'd and the cards rendered a price and a button above an empty list.
 *
 * A signup screen is the one place that must work before the account exists.
 *
 * Enterprise is deliberately absent: it has no list price and the server refuses to self-serve it,
 * so offering it here would be a button that cannot complete.
 */

function Features({ features }: { features: readonly PlanFeature[] }) {
  return (
    <ul className="gfg-uplan-features">
      {features.map((f) =>
        typeof f === "object" && f !== null && "divider" in f ? (
          <li key={`divider-${f.divider}`} className="gfg-uplan-divider">
            {f.divider}
          </li>
        ) : (
          <li key={f as string}>
            <CheckIcon className="gfg-uplan-check" />
            {f as string}
          </li>
        ),
      )}
    </ul>
  );
}

function PlanCard({
  display,
  ctaLabel,
  busyLabel,
  isSubmitting,
  onSelect,
  primary = false,
}: {
  display: PlanDisplay;
  ctaLabel: string;
  /** Shown in place of `ctaLabel` while the wizard is finishing — Starter finishes from here. */
  busyLabel?: string;
  isSubmitting: boolean;
  onSelect: () => void;
  primary?: boolean;
}) {
  return (
    <div className="gfg-uplan">
      <div className="gfg-uplan-name">{display.name}</div>
      <div className="gfg-uplan-price">
        <span className="gfg-uplan-amount">{display.amountLabel}</span>
        {display.unit && <span className="gfg-uplan-unit">{display.unit}</span>}
        <span className="gfg-uplan-billing">{display.billingLabel}</span>
      </div>
      <div className="gfg-uplan-cta">
        <button
          type="button"
          className={primary ? "gfg-uplan-btn primary" : "gfg-uplan-btn"}
          onClick={onSelect}
          disabled={isSubmitting}
        >
          {isSubmitting && busyLabel ? busyLabel : ctaLabel}
        </button>
      </div>
      <Features features={display.features} />
    </div>
  );
}

export function OnboardingPlans({
  isSubmitting = false,
  onPickStarter,
  onPickPlan,
}: {
  isSubmitting?: boolean;
  onPickStarter: () => void;
  onPickPlan: (plan: SelfServePlanKey) => void;
}) {
  return (
    <div className="gfg-ob-plans">
      <PlanCard
        display={PLAN_DISPLAY.starter}
        ctaLabel="Continue on Starter"
        busyLabel="Setting up your account…"
        isSubmitting={isSubmitting}
        onSelect={onPickStarter}
      />
      <PlanCard
        display={PLAN_DISPLAY.pro}
        ctaLabel={`Choose ${PLAN_DISPLAY.pro.name}`}
        isSubmitting={isSubmitting}
        onSelect={() => onPickPlan("pro")}
        primary
      />
      <PlanCard
        display={PLAN_DISPLAY.pro_plus}
        ctaLabel={`Choose ${PLAN_DISPLAY.pro_plus.name}`}
        isSubmitting={isSubmitting}
        onSelect={() => onPickPlan("pro_plus")}
        primary
      />
    </div>
  );
}
